import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { ArrowUp, Plus } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { ACCEPT, ACCEPT_ATTR } from "@/lib/intake";
import { filesFromDrop } from "@/lib/dropFiles";
import { AttachmentChip } from "@/components/intake/AttachmentChip";

const UPLOADING = new Set(["venter", "laster", "tolkes"]);

function SendButton({ onClick, disabled, testId }) {
  return (
    <button type="button" onClick={onClick} disabled={disabled} className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[hsl(var(--brand))] text-white transition-[transform,background-color,color] duration-150 ease-out hover:bg-[hsl(var(--brand-strong))] enabled:hover:scale-[1.04] enabled:active:scale-[0.96] disabled:bg-black/[0.06] disabled:text-foreground/30 focus-visible:ring-2 focus-visible:ring-ring/50" data-testid={testId} aria-label="Send">
      <ArrowUp className="h-[18px] w-[18px]" strokeWidth={2.4} />
    </button>
  );
}

/**
 * The one composer for starting and continuing work. `variant="hero"` is the start surface: a tall, quiet field – text
 * on top, «Legg til filer» and the arrow underneath – that grows with what you write. `variant="chat"` is the compact
 * line at the bottom of a conversation. Attachments sit inside while they upload; the arrow waits for them. With
 * `globalDrop` the page owns the drop (so a file can land anywhere) and the field only lights up.
 */
export const IntakeComposer = forwardRef(function IntakeComposer({ onSend, onFiles, busy, placeholder, autoFocus = false, className, testId = "home-composer", attachments = [], onRemoveAttachment, onRetryAttachment, hint, variant = "chat", globalDrop = false }, ref) {
  const [text, setText] = useState("");
  const [drag, setDrag] = useState(false);
  const taRef = useRef(null);
  const fileRef = useRef(null);
  const hero = variant === "hero";
  const uploading = attachments.some((a) => UPLOADING.has(a.status));
  const usable = attachments.some((a) => a.status !== "feil");
  const canSend = !busy && !uploading && Boolean(text.trim() || usable);

  useImperativeHandle(ref, () => ({
    focus: () => taRef.current?.focus(),
    seed: (t) => {
      setText(t);
      requestAnimationFrame(() => { const ta = taRef.current; if (!ta) return; ta.focus(); ta.setSelectionRange(ta.value.length, ta.value.length); });
    },
    pickFiles: () => fileRef.current?.click(),
  }));

  useEffect(() => {
    const ta = taRef.current;
    if (!ta) return;
    ta.style.height = "0px";
    ta.style.height = `${Math.min(Math.max(ta.scrollHeight, 24), hero ? 320 : 240)}px`;
  }, [text, hero]);

  const files = (list) => {
    const arr = Array.from(list || []);
    if (!arr.length) return;
    if (!arr.some((f) => ACCEPT.test(f.name))) return toast("Støttede filer: ZIP, PDF, Excel, Word, .msg-e-post, bilder og tegninger");
    onFiles(arr);
    taRef.current?.focus();
  };
  const submit = () => {
    if (!canSend) return;
    const t = text.trim();
    setText("");
    onSend(t);
  };
  const onDrop = (e) => {
    setDrag(false);
    if (globalDrop) return; // the page handles it – no double add
    e.preventDefault();
    filesFromDrop(e.dataTransfer).then(files);
  };
  const status = uploading ? "Laster opp …" : hint;

  const textarea = (
    <textarea
      ref={taRef}
      value={text}
      autoFocus={autoFocus}
      onChange={(e) => setText(e.target.value)}
      onPaste={(e) => { const fs = Array.from(e.clipboardData?.files || []); if (fs.length) { e.preventDefault(); files(fs); } }}
      onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); submit(); } }}
      placeholder={placeholder}
      rows={1}
      className={cn("caret-brand block w-full resize-none bg-transparent text-[16px] leading-[24px] text-foreground outline-none placeholder:text-foreground/40", hero ? "px-5 pb-1 pt-[18px]" : "min-h-[24px] flex-1 px-2 py-[7px]")}
      data-testid={`${testId}-input`}
      aria-label="Melding"
    />
  );
  const fileInput = <input ref={fileRef} type="file" multiple accept={ACCEPT_ATTR} className="hidden" onChange={(e) => { files(e.target.files); e.target.value = ""; }} data-testid="home-file-input" />;

  return (
    <div
      className={cn("relative bg-white", hero ? "composer-hero rounded-[24px]" : "composer-shadow rounded-[26px] border transition-[border-color,box-shadow] duration-150", !hero && (drag ? "border-primary/60 ring-4 ring-primary/10" : "border-black/[0.09] focus-within:border-black/[0.2]"), className)}
      onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
      onDragLeave={() => setDrag(false)}
      onDrop={onDrop}
      data-testid={testId}
      data-attachments={attachments.length}
      data-variant={variant}
      data-drag={drag ? "1" : "0"}
    >
      {attachments.length ? (
        <ul className={cn("flex flex-wrap gap-2", hero ? "px-4 pt-4" : "px-3 pt-3")} data-testid={`${testId}-attachments`}>
          {attachments.map((a) => <AttachmentChip key={a.key} row={a} onRemove={onRemoveAttachment} onRetry={onRetryAttachment} />)}
        </ul>
      ) : null}
      {hero ? (
        <>
          {textarea}
          <div className="flex items-center gap-2 px-3 pb-3 pt-2">
            {fileInput}
            <button type="button" onClick={() => fileRef.current?.click()} className="inline-flex h-9 shrink-0 items-center gap-1.5 rounded-full pl-2 pr-3 text-[13.5px] font-medium text-foreground/65 transition-colors duration-150 hover:bg-black/[0.05] hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring/40" aria-label="Legg ved filer" data-testid="home-chip-attach">
              <Plus className="h-[18px] w-[18px]" strokeWidth={2} /><span>Legg til filer</span>
            </button>
            {status ? <div className="min-w-0 flex-1 truncate text-center text-[12.5px] text-muted-foreground" data-testid={uploading ? `${testId}-uploading` : `${testId}-hint`}>{status}</div> : <div className="flex-1" />}
            <SendButton onClick={submit} disabled={!canSend} testId={`${testId}-send-button`} />
          </div>
        </>
      ) : (
        <>
          <div className="flex items-end gap-1 p-[7px]">
            {fileInput}
            <Tooltip>
              <TooltipTrigger asChild>
                <button type="button" onClick={() => fileRef.current?.click()} className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-foreground/65 transition-colors hover:bg-black/[0.05] hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring/40" aria-label="Legg ved filer" data-testid="home-chip-attach"><Plus className="h-5 w-5" strokeWidth={2} /></button>
              </TooltipTrigger>
              <TooltipContent side="bottom" className="rounded-lg px-2.5 py-1 text-[12px]">Dokumenter, mapper, bilder – eller slipp dem rett i feltet</TooltipContent>
            </Tooltip>
            {textarea}
            <SendButton onClick={submit} disabled={!canSend} testId={`${testId}-send-button`} />
          </div>
          {status ? <div className="-mt-1 px-4 pb-2.5 text-[12.5px] text-muted-foreground" data-testid={uploading ? `${testId}-uploading` : `${testId}-hint`}>{status}</div> : null}
        </>
      )}
    </div>
  );
});
